import React from "react";
import ArrowRight from "../../../../../shared/svg/arrowRight";
import BoxWithDownArrowRight from "../../../../../shared/svg/boxWithDownArrowRight";
import { Delete } from "../../../../../shared/svg/delete";
import EditIcon from "../../../../../shared/svg/editIcon";
import EditPencilIcon from "../../../../../shared/svg/editPencil";
import EyeIcon from "../../../../../shared/svg/eye";
import ThreeDotIconWithContextMenu from "../../../threeDotsWithContextMenu/threeDotsWithContextMenu";

/**
 * Supported icon variants that an `IconCell` can render. To add a new
 * variant: add it to this enum and register an icon for it in
 * {@link ICON_VARIANT_TO_ELEMENT}.
 */
export enum IconCellVariant {
  DELETE = "delete",
  EDIT = "edit",
  VIEW = "view",
  ARROW_RIGHT = "arrow_right",
  EDIT_PENCIL = "edit_pencil",
  BOX_ARROW_IN_DOWN_RIGHT_RED = "box_arrow_in_down_right_red",
  BOX_ARROW_IN_DOWN_RIGHT_BLUE = "box_arrow_in_down_right_blue",
  NONE = "none",
}

/**
 * Map of `IconCellVariant` to the React element rendered for that variant.
 * Centralising the mapping here keeps `IconCell` agnostic of which icons
 * exist — callers only need to know the variant key.
 */
const ICON_VARIANT_TO_ELEMENT: Record<IconCellVariant, React.ReactNode> = {
  [IconCellVariant.DELETE]: <Delete />,
  [IconCellVariant.EDIT]: <EditIcon />,
  [IconCellVariant.VIEW]: <EyeIcon />,
  [IconCellVariant.ARROW_RIGHT]: <ArrowRight />,
  [IconCellVariant.EDIT_PENCIL]: (
    <EditPencilIcon stroke="#0056D6" height={16} width={16} />
  ),
  [IconCellVariant.BOX_ARROW_IN_DOWN_RIGHT_RED]: (
    <BoxWithDownArrowRight fillColor="#F50400" />
  ),
  [IconCellVariant.BOX_ARROW_IN_DOWN_RIGHT_BLUE]: <BoxWithDownArrowRight />,
  [IconCellVariant.NONE]: null,
};

/**
 * Shape of the cell value consumed by `IconCell`. The cell either renders
 * a registered icon (when `variant` is set) or a three-dot context menu
 * built from `contextMenuItems`. `rowData` is forwarded to the click
 * handler when the variant icon is clicked.
 */
export interface IconCellValue {
  contextMenuItems?: any;
  variant?: IconCellVariant;
  rowData?: any;
}

interface IconCellProps {
  value: IconCellValue;
  handleIconCellClick?: (data: any) => void;
  [key: string]: any;
}

/**
 * Renders a table cell that shows either a registered icon (when
 * `value.variant` is provided) or the default three-dot context menu.
 *
 * The icon for a variant is resolved via the internal
 * {@link ICON_VARIANT_TO_ELEMENT} map — the cell value only carries the
 * variant key, never an icon node directly. Clicks on the icon propagate
 * up via `handleIconCellClick`.
 *
 * @param value - Cell value carrying an optional `variant` and the
 *   `contextMenuItems` used for the default three-dot menu
 * @param handleIconCellClick - Optional click handler invoked with
 *   `value.rowData` when the variant icon is clicked
 */
export const IconCell: React.FC<IconCellProps> = ({
  value,
  handleIconCellClick,
}) => {
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    handleIconCellClick?.(value?.rowData);
  };

  const variantIcon = value?.variant
    ? ICON_VARIANT_TO_ELEMENT[value.variant]
    : null;

  if (value?.variant === IconCellVariant.NONE) {
    return <div className="table-cell-text-with-context-menu w-full" />;
  }

  if (variantIcon) {
    return (
      <div
        className="table-cell-text-with-context-menu w-full"
        onClick={handleClick}
        style={{ cursor: "pointer" }}
      >
        {variantIcon}
      </div>
    );
  }

  return (
    <div className="table-cell-text-with-context-menu w-full">
      <ThreeDotIconWithContextMenu menuData={value?.contextMenuItems} />
    </div>
  );
};

export default IconCell;
